import DeleteIcon from "@assets/icons/new/delete_32px.svg?react";
import DownloadIcon from "@assets/icons/new/download_24px.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { ReactSetStateFunction } from "@custom-types/types";
import { IElementAttachment } from "@faro-lotv/ielement-types";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { Box, IconButtonProps, Typography } from "@mui/material";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { DownloadUtils } from "@stellar/utils";
import { getFileExtension, isImageFileType } from "@utils/file-utils";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { cloneDeep, findIndex } from "lodash";
import { useState } from "react";
import { Dialog, neutral } from "@flat-ui";

interface Props {
  /** The attachment item to display */
  attachment: IElementAttachment;

  /** Function to update the downloading state of the attachment */
  setIsDownloading: (value: boolean) => void;

  /**
   *  Indicates the component or context that triggers the event,
   *  For example: "Preview Attachment Dialog" or "Side Panel".
   */
  eventSource: string;

  /** Size in pixels for the outer button. */
  buttonSize: string | number;

  /** Size in pixels for the icon. */
  iconSize?: string | number;

  /** Optional props to be forwarded to the IconButtonProps.  */
  iconBtnProps?: IconButtonProps;

  /** Function to set the selected attachments */
  setSelectedAttachments: ReactSetStateFunction<Set<string>>;
}

/**
 * Component that rendering action buttons for managing an attachment item,
 * including downloading the attachment and deleting it (if the user has permission).
 *
 * @param {AttachmentItemActionsProps} Props - The properties for the component, including the attachment data,
 * a function to update the downloading state and a function to control the delete modal visibility.
 * @returns {JSX.Element} The rendered component.
 */
export function AttachmentItemActions({
  attachment,
  setIsDownloading,
  eventSource,
  buttonSize,
  iconSize,
  iconBtnProps,
  setSelectedAttachments,
}: Props): JSX.Element {
  const {
    hasPermissionToEditMarkup,
    projectId,
    markups,
    selectedMarkup,
    setIsMarkupUpdating,
    updateMarkups,
    attachments,
    updateAttachments,
  } = useMarkupContext();

  const { deleteAttachmentToMarkup } = useProjectMarkupUpdate({ projectId });
  const { refreshAttachmentIfExpired, isAttachmentExpired } =
    useRefreshAttachment();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { trackEvent } = useTrackEvent();

  // Get the file extension from the attachment's name and determine if it's an image file type
  const fileExtension = getFileExtension(attachment.name);
  const isImage = isImageFileType(fileExtension);

  /**
   * Downloads a file from a given URL and saves it to the user's device.
   *
   * This function performs the following steps:
   * 1. Initiates the download of the file from the specified URL as a Blob object.
   * 2. Provides optional callback functions to track the download progress, start, and end events.
   * 3. Creates a downloadable link for the Blob object.
   * 4. Triggers the browser to download the file using the created link.
   *
   * @returns {Promise<void>} A promise that resolves when the download and save operation is complete.
   */
  async function downloadAndSaveFile(): Promise<void> {
    trackEvent({
      name: `${AnnotationEvents.downloadAttachment}`,
      props: {
        eventSource: eventSource,
        fileType: isImage ? "image" : "other",
        fileExtension,
      },
    });

    const isExpired = isAttachmentExpired(attachment);

    let currentAttachment = attachment;

    if (isExpired) {
      setIsDownloading(true);

      try {
        const updatedAttachments = await refreshAttachmentIfExpired([
          attachment.id,
        ]);
        currentAttachment = updatedAttachments[0];
      } catch {
        setIsDownloading(false);
        return;
      }
    }

    if (!currentAttachment.signedUri || !currentAttachment.signedUriExpiresOn) {
      return;
    }

    const fileBlob = await DownloadUtils.downloadFilePromise(
      currentAttachment.signedUri,
      {
        onFileDownloadStart: () => setIsDownloading(true),
        onFileDownloadEnd: () => setIsDownloading(false),
      }
    );

    const fileUrl = URL.createObjectURL(fileBlob);
    DownloadUtils.downloadFile(attachment.name, fileUrl);
  }

  /**
   * Deletes an attachment from the project and updates the local copy of the project.
   *
   * This function performs the following steps:
   * 1. Creates a mutation to delete the attachment using its ID.
   * 2. Applies the mutation to the project API client to delete the attachment.
   * 3. Fetches the updated sub-tree of elements related to the attachment and updates the local state.
   *
   * @returns {Promise<void>} A promise that resolves when the attachment deletion and state update are complete.
   */
  async function deleteAttachment(): Promise<void> {
    trackEvent({
      name: `${AnnotationEvents.deleteAttachment}`,
      props: {
        eventSource: eventSource,
        fileType: isImage ? "image" : "other",
        fileExtension,
      },
    });

    setIsDeleting(true);
    setIsMarkupUpdating(true);

    const mutation = await deleteAttachmentToMarkup(attachment);

    if (!mutation || !selectedMarkup) {
      setIsDeleting(false);
      setIsMarkupUpdating(false);
      return;
    }

    // Updated attachments of the annotation
    const updatedAttachments = attachments.filter(
      (attachment) => attachment.id !== mutation.elementId
    );
    updateAttachments(updatedAttachments);

    // Also update the value of the attachmentsCount property of the annotation, which is used in the annotations table
    const newMarkups = cloneDeep(markups);
    const markupIndex = findIndex(newMarkups, { id: selectedMarkup.id });
    newMarkups[markupIndex].attachmentsCount = updatedAttachments.length;
    updateMarkups(newMarkups);

    // Remove the attachment from the selected attachments list if it's selected, then update the state
    setSelectedAttachments((prevSelected) => {
      if (!prevSelected.has(attachment.id)) {
        return prevSelected;
      }
      const updatedSet = new Set(prevSelected);
      updatedSet.delete(attachment.id);
      return updatedSet.size > 0 ? updatedSet : new Set<string>();
    });

    setIsDeleting(false);
    setIsMarkupUpdating(false);
  }

  return (
    <Box sx={{ display: "flex" }}>
      <SphereIconButton
        buttonSize={buttonSize}
        iconSize={iconSize}
        component={DownloadIcon}
        onClick={downloadAndSaveFile}
        dataTestId="markup-side-panel-attachment-item-download-icon"
        iconButtonProps={iconBtnProps ? iconBtnProps : {}}
      />
      {hasPermissionToEditMarkup && (
        <>
          <SphereActionDivider />
          <SphereIconButton
            buttonSize={buttonSize}
            iconSize={iconSize}
            component={DeleteIcon}
            onClick={() => setIsDeleteModalOpen(true)}
            dataTestId="markup-side-panel-attachment-item-delete-icon"
            iconButtonProps={iconBtnProps ? iconBtnProps : {}}
          />
        </>
      )}
      <Dialog
        title="Delete attachment?"
        confirmText="Delete"
        open={isDeleteModalOpen}
        onConfirm={deleteAttachment}
        onCancel={() => setIsDeleteModalOpen(false)}
        showSpinner={isDeleting}
        variant="danger"
      >
        <Typography
          data-testid="delete-annotation-attachment-dialog-content"
          sx={{ fontSize: "14px", color: neutral[800] }}
        >
          This will remove the attachment "
          <var id="attachment-name">{attachment.name}</var>" from your project.
        </Typography>
      </Dialog>
    </Box>
  );
}
